import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaTruck, FaTimes } from "react-icons/fa";
import axios from "axios";
import "../styles/trackOrder.css";
import ShippingProgress from "../components/ShippingProgress";
import formatMoney from "../components/formatMoney";
import { useImage } from "../contexts/ImageContext";
import { API_URL } from "../variables";

const TrackOrder = () => {
  const [orderId, setOrderId] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { getImageUrl } = useImage();

  const handleTrackOrder = async (e) => {
    e.preventDefault();
    if (!orderId.trim()) {
      setError("Please enter an order ID");
      return;
    }

    setLoading(true);
    setError("");
    setOrder(null);

    try {
      const response = await axios.post(`${API_URL}/api/orders/get`, {
        order_id: orderId.trim(),
      });

      if (response.data) {
        setOrder(response.data);
      } else {
        setError("Order not found. Please check your order ID and try again.");
      }
    } catch (error) {
      console.error("Error tracking order:", error);
      if (error.response?.status === 404) {
        setError("Order not found. Please check your order ID and try again.");
      } else {
        setError(
          "An error occurred while tracking your order. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClearOrder = () => {
    setOrder(null);
    setOrderId("");
    setError("");
  };

  return (
    <div className="track-order-container">
      <div className="track-order-header">
        <h1>Track Your Order</h1>
        <p>
          Enter your order ID to track the status and progress of your order
        </p>
      </div>

      <div className="track-order-form-container">
        <form onSubmit={handleTrackOrder} className="track-order-form">
          <div className="form-group">
            <label htmlFor="orderId">Order ID</label>
            <div className="input-group">
              <input
                type="text"
                id="orderId"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="Enter your order ID (e.g., ORD123456)"
                className="order-id-input"
                disabled={loading}
              />
              <button
                type="submit"
                className="track-button"
                disabled={loading || !orderId.trim()}
              >
                {loading ? (
                  <div className="loading-spinner"></div>
                ) : (
                  <FaSearch />
                )}
                {loading ? "Tracking..." : "Track Order"}
              </button>
            </div>
          </div>
        </form>

        {error && (
          <div className="error-message">
            <FaTimes />
            <span>{error}</span>
          </div>
        )}
      </div>

      {order && (
        <div className="order-details-container">
          <div className="order-header">
            <div className="order-header-info">
              <h2>Order {order.order_id}</h2>
              <p className="order-date">
                Placed on{" "}
                {new Date(order.created_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
              <div className="order-status-badges">
                <div className="status-badge">
                  <span className="status-label">Order Status</span>
                  <span
                    className={`order-status-text ${order.status.toLowerCase()}`}
                  >
                    {order.status}
                  </span>
                </div>
                <div className="status-badge">
                  <span className="status-label">Payment Status</span>
                  <span
                    className={`order-status-text ${order.payment_status.toLowerCase()}`}
                  >
                    {order.payment_status}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={handleClearOrder} className="clear-order-btn">
              <FaTimes />
              Track Another Order
            </button>
          </div>

          <div className="order-content">
            <div className="order-summary-section">
              <h3>Order Summary</h3>
              <div className="order-summary-details">
                <div className="summary-row">
                  <span>Subtotal:</span>
                  <span>{formatMoney(order.total_price || 0)}</span>
                </div>
                {order.shipping_fees && (
                  <div className="summary-row">
                    <span>Shipping Fee:</span>
                    <span>{formatMoney(order.shipping_fees)}</span>
                  </div>
                )}
                {order.applied_coupon_code &&
                  order.coupon_discount_amount > 0 && (
                    <div className="summary-row coupon-discount">
                      <span>
                        Coupon Discount ({order.applied_coupon_code}):
                      </span>
                      <span>-{formatMoney(order.coupon_discount_amount)}</span>
                    </div>
                  )}
                <div className="summary-row total">
                  <span>Total:</span>
                  <span>
                    {formatMoney(
                      order.final_total ||
                        (order.total_price || 0) + (order.shipping_fees || 0)
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="order-items-section">
              <h3>Items Ordered</h3>
              {Array.isArray(order.order_items) &&
              order.order_items.length > 0 ? (
                order.order_items.map((item) => (
                  <div key={item.order_item_id} className="order-item">
                    <div className="item-image">
                      <img
                        src={getImageUrl(item.product.image)}
                        alt={item.product.name}
                      />
                    </div>
                    <div className="item-details">
                      <h4 className="item-name">{item.product.name}</h4>
                      <div className="item-info">
                        <span className="item-quantity">
                          Quantity: {item.quantity}
                        </span>
                        <span className="item-price">
                          Price: {formatMoney(item.price_at_purchase)}
                        </span>
                        {item.option && (
                          <span className="item-option">
                            Option: {item.option.name}
                          </span>
                        )}
                        {item.selected_color ? (
                          <span className="item-option">
                            Color: {item.selected_color}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p>No items found for this order.</p>
              )}
            </div>

            <ShippingProgress orderProgress={order.order_progress} />

            <div className="delivery-info-section">
              <h3>Delivery Information</h3>
              <p>Address: {order.address || "Not Provided"}</p>
              {order.tracking_url && (
                <p>
                  <a
                    href={order.tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tracking-link"
                  >
                    <FaTruck />
                    Track your shipment
                  </a>
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackOrder;
